import { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Menu, X, Instagram } from "lucide-react";
import { SITE } from "@/lib/site";
import { scrollToSection, scrollToTop } from "@/lib/scroll";

const links = [
  { label: "The Gap", href: "#gap" },
  { label: "About", href: "#about" },
  { label: "Ways to Work", href: "#services" },
  { label: "Quiz", href: "#quiz" },
  { label: "FAQ", href: "#faq" },
];

const Navbar = () => {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 40);
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const go = (href) => {
    setOpen(false);
    scrollToSection(href);
  };

  return (
    <motion.header
      initial={{ y: -80, opacity: 0 }}
      animate={{ y: 0, opacity: 1 }}
      transition={{ duration: 0.8, ease: [0.22, 1, 0.36, 1], delay: 0.2 }}
      data-testid="navbar"
      className={`fixed top-0 left-0 right-0 z-50 transition-colors duration-500 ${
        scrolled ? "bg-primary/95 backdrop-blur-md" : "bg-transparent"
      }`}
    >
      <nav className="mx-auto flex max-w-[1400px] items-center justify-between px-6 py-5 md:px-12">
        <button
          data-testid="nav-logo"
          onClick={() => scrollToTop()}
          className="font-display text-xl font-semibold tracking-tight text-primary-foreground md:text-2xl"
        >
          Dr. Amanjot Kaur<span className="text-secondary">.</span>
        </button>

        <div className="hidden items-center gap-9 md:flex">
          {links.map((l) => (
            <button
              key={l.href}
              data-testid={`nav-${l.href.slice(1)}`}
              onClick={() => go(l.href)}
              className="text-sm font-medium uppercase tracking-[0.12em] text-primary-foreground/70 transition-colors hover:text-secondary"
            >
              {l.label}
            </button>
          ))}
          <button
            data-testid="nav-book-btn"
            onClick={() => go("#book")}
            className="rounded-full bg-secondary px-6 py-2.5 text-sm font-semibold text-secondary-foreground transition-transform duration-300 hover:scale-[1.04]"
          >
            Book a Session
          </button>
        </div>

        <div className="flex items-center gap-4 md:hidden">
          <a
            href={SITE.instagram}
            target="_blank"
            rel="noreferrer"
            data-testid="nav-instagram-mobile"
            className="text-primary-foreground"
          >
            <Instagram size={22} />
          </a>
          <button
            data-testid="nav-menu-toggle"
            onClick={() => setOpen((v) => !v)}
            className="text-primary-foreground"
          >
            {open ? <X size={26} /> : <Menu size={26} />}
          </button>
        </div>
      </nav>

      <AnimatePresence>
        {open && (
          <motion.div
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: "auto", opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.35 }}
            className="overflow-hidden bg-primary md:hidden"
            data-testid="mobile-menu"
          >
            <div className="flex flex-col gap-1 px-6 pb-8 pt-2">
              {links.map((l) => (
                <button
                  key={l.href}
                  onClick={() => go(l.href)}
                  className="border-b border-primary-foreground/10 py-4 text-left font-display text-2xl text-primary-foreground"
                >
                  {l.label}
                </button>
              ))}
              <button
                onClick={() => go("#book")}
                className="mt-4 rounded-full bg-secondary px-6 py-3 font-semibold text-secondary-foreground"
              >
                Book a Session
              </button>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </motion.header>
  );
};

export default Navbar;
